import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigationStore } from "@/stores/navigation"
import CustomerLogos from "@/components/common/CustomerLogos"
import {
  CalendarDays,
  Users2,
  ClipboardList,
  FileCheck,
  Bell,
  Smartphone,
  Link as LinkIcon,
  ShieldCheck,
  FileText,
  Lock,
  Stethoscope,
  GraduationCap,
  Building2,
  ShoppingCart,
  Gavel,
  MessageSquare,
  FileCheck2,
  BarChart3,
} from "lucide-react"

const ConsumerCX: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  const features = [
    {
      title: t("consumercx.features.items.selfRegistration.title"),
      body: t("consumercx.features.items.selfRegistration.body"),
      Icon: Users2,
    },
    {
      title: t("consumercx.features.items.selfScheduling.title"),
      body: t("consumercx.features.items.selfScheduling.body"),
      Icon: CalendarDays,
    },
    {
      title: t("consumercx.features.items.requestServices.title"),
      body: t("consumercx.features.items.requestServices.body"),
      Icon: ClipboardList,
    },
    {
      title: t("consumercx.features.items.digitalForms.title"),
      body: t("consumercx.features.items.digitalForms.body"),
      Icon: FileCheck,
    },
    {
      title: t("consumercx.features.items.notifications.title"),
      body: t("consumercx.features.items.notifications.body"),
      Icon: Bell,
    },
    {
      title: t("consumercx.features.items.mobileFriendly.title"),
      body: t("consumercx.features.items.mobileFriendly.body"),
      Icon: Smartphone,
    },
    {
      title: t("consumercx.features.items.workflowParticipation.title"),
      body: t("consumercx.features.items.workflowParticipation.body"),
      Icon: ClipboardList,
    },
    {
      title: t("consumercx.features.items.standaloneIntegrated.title"),
      body: t("consumercx.features.items.standaloneIntegrated.body"),
      Icon: LinkIcon,
    },
    {
      title: t("consumercx.features.items.analyticsInsights.title"),
      body: t("consumercx.features.items.analyticsInsights.body"),
      Icon: BarChart3,
    },
  ]

  const useCases = [
    {
      title: t("consumercx.useCases.items.hospitalPortals.title"),
      body: t("consumercx.useCases.items.hospitalPortals.body"),
      Icon: Stethoscope,
    },
    {
      title: t("consumercx.useCases.items.employerWellness.title"),
      body: t("consumercx.useCases.items.employerWellness.body"),
      Icon: Building2,
    },
    {
      title: t("consumercx.useCases.items.education.title"),
      body: t("consumercx.useCases.items.education.body"),
      Icon: GraduationCap,
    },
    {
      title: t("consumercx.useCases.items.retailClinics.title"),
      body: t("consumercx.useCases.items.retailClinics.body"),
      Icon: ShoppingCart,
    },
    {
      title: t("consumercx.useCases.items.correctionsJustice.title"),
      body: t("consumercx.useCases.items.correctionsJustice.body"),
      Icon: Gavel,
    },
    {
      title: t("consumercx.useCases.items.conciergeMedicine.title"),
      body: t("consumercx.useCases.items.conciergeMedicine.body"),
      Icon: CalendarDays,
    },
    {
      title: t("consumercx.useCases.items.econsultAdvice.title"),
      body: t("consumercx.useCases.items.econsultAdvice.body"),
      Icon: MessageSquare,
    },
    {
      title: t("consumercx.useCases.items.formsConsent.title"),
      body: t("consumercx.useCases.items.formsConsent.body"),
      Icon: FileText,
    },
  ]

  const whyBullets = [
    {
      title: t("consumercx.whyConsumercx.items.consumerGradeSimplicity.title"),
      details: t(
        "consumercx.whyConsumercx.items.consumerGradeSimplicity.details",
        { returnObjects: true }
      ) as string[],
      Icon: Smartphone,
    },
    {
      title: t("consumercx.whyConsumercx.items.lowerNoShows.title"),
      details: t("consumercx.whyConsumercx.items.lowerNoShows.details", {
        returnObjects: true,
      }) as string[],
      Icon: CalendarDays,
    },
    {
      title: t("consumercx.whyConsumercx.items.embeddedWorkflows.title"),
      details: t("consumercx.whyConsumercx.items.embeddedWorkflows.details", {
        returnObjects: true,
      }) as string[],
      Icon: ClipboardList,
    },
    {
      title: t("consumercx.whyConsumercx.items.builtForHealth.title"),
      details: t("consumercx.whyConsumercx.items.builtForHealth.details", {
        returnObjects: true,
      }) as string[],
      Icon: ShieldCheck,
    },
  ]

  const faqs = [
    {
      q: t("consumercx.faq.items.integration.question"),
      a: t("consumercx.faq.items.integration.answer"),
      Icon: LinkIcon,
    },
    {
      q: t("consumercx.faq.items.security.question"),
      a: t("consumercx.faq.items.security.answer"),
      Icon: ShieldCheck,
    },
    {
      q: t("consumercx.faq.items.useCases.question"),
      a: t("consumercx.faq.items.useCases.answer"),
      Icon: ClipboardList,
    },
    {
      q: t("consumercx.faq.items.implementation.question"),
      a: t("consumercx.faq.items.implementation.answer"),
      Icon: FileCheck2,
    },
    {
      q: t("consumercx.faq.items.branding.question"),
      a: t("consumercx.faq.items.branding.answer"),
      Icon: Smartphone,
    },
    {
      q: t("consumercx.faq.items.pricing.question"),
      a: t("consumercx.faq.items.pricing.answer"),
      Icon: BarChart3,
    },
  ]

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="min-h-screen w-full text-white">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-t border-slate-200 p-3 sm:hidden">
        <div className="flex gap-3">
          <button
            onClick={handleBookDemo}
            className="flex-1 text-center bg-[#395091] text-white font-semibold px-4 py-3 rounded-lg shadow-md"
          >
            {t("common.bookDemo")}
          </button>
          <a
            href="#case-study"
            className="flex-1 text-center bg-white text-[#395091] font-semibold px-4 py-3 rounded-lg border border-[#395091]/30 hover:bg-gray-50 transition"
          >
            {t("vaxmanagercx.mobileCta.caseStudy")}
          </a>
        </div>
      </div>

      {/* HERO + BODY COMBINED */}
      <main className="bg-white text-slate-800">
        {/* HERO with robust tie-dye fallback */}
        <header
          className="relative w-full text-white overflow-hidden"
          style={{
            backgroundImage: `
              repeating-conic-gradient(
                from 0deg,
                #1e3a8a 0deg 15deg,   /* navy */
                #2563eb 15deg 30deg,  /* blue */
                #dc2626 30deg 45deg,  /* red */
                #f97316 45deg 60deg,  /* orange */
                #16a34a 60deg 75deg,  /* green */
                #9333ea 75deg 90deg,  /* purple */
                #1e3a8a 90deg 105deg,
                #2563eb 105deg 120deg,
                #dc2626 120deg 135deg,
                #f97316 135deg 150deg,
                #16a34a 150deg 165deg,
                #9333ea 165deg 180deg
              ),
              radial-gradient(circle at center, rgba(255,255,255,0.15), transparent 70%)
            `,
            backgroundSize: "300% 300%",
            backgroundBlendMode: "screen",
            animation: "swirl 80s linear infinite",
          }}
        >
          <style>{`
            @keyframes swirl {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            {/* Product Wordmark */}
            <div className="mb-8 sm:mb-10">
              <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
                <span >consumer</span>
                <span className="font-bold text-[#7AC943]">CX</span>
                <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">
                  ™
                </sup>
              </h2>
            </div>

            {/* Headline & Subcopy */}
            <div className="max-w-4xl bg-black/20 backdrop-blur-sm rounded-xl p-4 sm:p-5">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
                {t("consumercx.hero.title")}
              </h1>
              <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
                {t("consumercx.hero.subtitle")}
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
                <a
                  href="#case-study"
                  className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t("consumercx.hero.readCaseStudy")}
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Features Section */}
        <section
          id="features"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Customer Access to the Services You Provide
          </h3>
          <p className="text-slate-600 mb-6">
            Expose the right services to the right audiences with configurable rules. Reduce staff effort while improving access, transparency, and satisfaction.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white/70 backdrop-blur-[1px]"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#F0F5FF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {title}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section
          id="use-cases"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EDF7FF] to-[#F9FBFF] rounded-3xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">
            {t("consumercx.useCases.title")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {useCases.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {title}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Case Study + Why */}
        <section
          id="case-study"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 lg:scroll-mt-28"
        >
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
            {/* Case Study */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">
                {t("consumercx.caseStudy.title")}
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                {t("consumercx.caseStudy.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href="/use-case-finder?Org=TELUS"
                  className="inline-block rounded-lg bg-[#395091] text-white px-5 py-3 font-semibold hover:opacity-90 text-center"
                >
                  {t("common.viewCaseStudy")}
                </a>
                <a
                  href="/use-case-finder"
                  className="inline-block text-[#395091] font-semibold hover:underline text-center"
                >
                  {t("consumercx.caseStudy.seeMoreStories")}
                </a>
              </div>
              {/* Client logos row */}
              <CustomerLogos logoUids={["rady", "dfci", "nhs"]} />
            </div>

            {/* Why ConsumerCX */}
            <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#EDF2FF] via-white to-[#F9FAFB]">
              <h4 className="font-bold text-slate-900 mb-3 text-3xl md:text-4xl">
                {t("consumercx.whyConsumercx.title")}
              </h4>
              <p className="text-slate-700 text-base leading-relaxed mb-4">
                {t("consumercx.whyConsumercx.description")}
              </p>
              <ul className="space-y-3 text-base">
                {whyBullets.map(({ title, details, Icon }, idx) => (
                  <li key={idx}>
                    <details className="group">
                      <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                        <Icon className="w-5 h-5 text-[#395091]" /> {title}
                      </summary>
                      <div className="mt-2 text-slate-600">
                        <ul className="list-disc ml-5 space-y-1">
                          {details.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("consumercx.trust.title")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("consumercx.trust.hipaa")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FileCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("consumercx.trust.soc2")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("consumercx.trust.sso")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("consumercx.trust.gdpr")}
              </span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("consumercx.faq.title")}
          </h3>
          <ul className="space-y-4">
            {faqs.map(({ q, a, Icon }, i) => (
              <li key={i} className="border border-slate-200 rounded-lg p-4">
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                    <Icon className="w-5 h-5 text-[#395091]" /> {q}
                  </summary>
                  <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* Final CTA */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
            }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                  {t("consumercx.cta.title")}
                </h3>
                <p className="opacity-90 text-base sm:text-lg">
                  {t("consumercx.cta.subtitle")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button
                  onClick={handleBookDemo}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          {t("consumercx.footer")}
        </section>
      </main>
    </div>
  )
}

export default ConsumerCX
